import { Suspense, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import './App.css';
import Header from './components/core/header/Header';
import 'bootstrap/dist/css/bootstrap.min.css';
import { checkAppAccessibility } from './utils/accessibility-check';
import PermissionError from './components/error/permission-error/PermissionError';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { TranslateEn } from './assets/i18n/translateEn';
import { TranslateFr } from './assets/i18n/translateFr';
import { ToastContainer } from 'react-toastify';
import AppRoutes from './utils/routes';
import { postLoginLogout } from './services/UserAuthorization';
import { FeatureProvider, useFeatures } from './utils/featureContext';

/**
  Auth0App Component
  
  This is the main authentication component using Auth0.
  
  Features:
  1. Login: Automatically redirects to Auth0 IDP for authentication
  2. Logout: Clears session and logs out from Auth0 IDP
  3. Token Refresh: Automatically handled by Auth0 SDK (getAccessTokenSilently)
  4. User Info: Logs user object to console after successful login
  5. Access Control: Checks user type and feature list
  6. Audit Logging: Logs login/logout events
 */

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: TranslateEn },
    fr: { translation: TranslateFr }
  },
  lng: "en",
  fallbackLng: "en",
  interpolation: { escapeValue: false }
});

const LOGIN_AUDIT_KEY = "LOGIN_AUDIT_DONE";

const getUserNameFromEmail = (email) => {
  if (!email || typeof email !== "string") return "";
  return email.split("@")[0];
};

const FeatureLoader = ({ userName, children }) => {
  const { loadFeatures } = useFeatures();

  useEffect(() => {
    if (userName) {
      loadFeatures(userName);
    }
  }, [userName, loadFeatures]);

  return children;
};

function Auth0App() {
  const { 
    isAuthenticated, 
    user, 
    loginWithRedirect, 
    logout, 
    isLoading,
    getAccessTokenSilently 
  } = useAuth0();

  const [userName, setUserName] = useState("");
  const [loginAuditDone, setLoginAuditDone] = useState(false);


  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('Full User Object:', user);

      const extractedUserName = getUserNameFromEmail(user.email);
      setUserName(extractedUserName);
      localStorage.setItem('auth0Username', extractedUserName);

      // eslint-disable-next-line no-restricted-globals
      window.getAuth0Token = getAccessTokenSilently;

      
      const alreadyLogged = sessionStorage.getItem(LOGIN_AUDIT_KEY);
      if (!alreadyLogged && !loginAuditDone) {
        postLoginLogout({
          action: "login",
          userName: extractedUserName,
        })
          .then(() => {
            sessionStorage.setItem(LOGIN_AUDIT_KEY, "true");
            setLoginAuditDone(true);
          })
          .catch((err) => {
            console.error("Login audit failed", err);
          });
      }
    }
  }, [isAuthenticated, user, getAccessTokenSilently, loginAuditDone]);

  
  const canUserAccess = () => {
    return user && checkAppAccessibility(user['UserType'], user['FeatureList']);
  };

  
  
  const handleLogout = async () => {
    try {
      await postLoginLogout({
        action: "logout",
        userName
      });
    } catch (err) {
      console.error("Logout audit failed", err);
    } finally {
      sessionStorage.removeItem(LOGIN_AUDIT_KEY);
      localStorage.removeItem('auth0Username');
      // eslint-disable-next-line no-restricted-globals
      delete window.getAuth0Token;
      logout({ 
        logoutParams: {
          // eslint-disable-next-line no-restricted-globals
          returnTo: window.location.origin 
        }
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    // Try silent authentication first (for new tabs/windows)
    // This prevents unnecessary redirect loops
    loginWithRedirect({
      appState: {
        // eslint-disable-next-line no-restricted-globals
        returnTo: window.location.pathname + window.location.search
      }
    });
    return (
      <div>
        Redirecting to login...
      </div>
    );
  }

  
  return (
    user && canUserAccess() ? (
      <FeatureProvider>
        <FeatureLoader userName={userName}>
          <Suspense fallback="loading...">
            <div className="app-shell">
              <Header signout={handleLogout} user={user} />

              
              <main className="app-main">
                <AppRoutes />
              </main>

              <ToastContainer
                autoClose={3000}
                hideProgressBar
                newestOnTop={false}
                closeOnClick
                pauseOnFocusLoss
                draggable
                pauseOnHover
                style={{ top: '100px' }}
              />
            </div>
          </Suspense>
        </FeatureLoader>
      </FeatureProvider>
    ) : (
      <PermissionError />
    )
  );
}

export default Auth0App;
